import React, { Fragment, useEffect, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import classNames from "classnames";
import Checkbox from "./Checkbox";
import Loader from "../Loader";
import { fetchFromAPI } from "../../utils/api";

export default function PlanView({
    isAuthenticated,
    subscription,
    setCancelSubModalIsOpen,
    setCheckInModalIsOpen,
    setSubscriptionToCancel,
    setOpenCheckInSlide,
    setSlideCheckInId,
}) {
    const [loading, setLoading] = useState(true);
    // Using underscore prefix to indicate it's used in error handling but not directly referenced
    const [_error, setError] = useState(null);
    const [data, setData] = useState([]);
    const [unChecked, setUnChecked] = useState(0);

    useEffect(() => {
        async function getCheckIns() {
            try {
                const result = await fetchFromAPI(`/checkins.php?subscription_id=${subscription.id}`);
                setError(null);
                return result;
            } catch (err) {
                console.log(err);
                setError(err.message || 'Failed to fetch check-ins');
            }
        }
        getCheckIns().then((checkins) => {
            // Handle both null/undefined and empty array cases
            const checkinsArray = checkins || [];
            setData(checkinsArray);
            
            // Calculate remaining checkboxes, ensuring it's never negative
            const remaining = Math.max(0, subscription.plan_classes - checkinsArray.length - 1);
            setUnChecked(remaining);
            setLoading(false);
        });
    }, [subscription]);

    useEffect(() => {
        // Calculate remaining checkboxes, ensuring it's never negative
        const remaining = Math.max(0, subscription.plan_classes - data.length - 1);
        setUnChecked(remaining);
    }, [data, subscription.plan_classes]);

    return (
        <>
            <div className="relative max-w-3xl mx-auto mb-6 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl lg:p-8 bg-white p-6 shadow-md shadow-gray-200">
                <div className="-ml-4 -mt-4 items-center">
                    <div className="ml-4 mt-4 w-full">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            {subscription.plan_name}
                        </h3>
                        <p className="text-sm text-gray-500">
                            Expires on {subscription.plan_expiry}
                        </p>
                    </div>
                    {loading && <Loader />}
                    {!loading && (
                        <div className="pt-4">
                            {[...Array(data.length)].map(
                                (elementInArray, index) => (
                                    <Checkbox
                                        key={index}
                                        style="checked"
                                        setOpenCheckInSlide={
                                            isAuthenticated ? setOpenCheckInSlide : null
                                        }
                                        setSlideCheckInId={
                                            isAuthenticated ? setSlideCheckInId : null
                                        }
                                        checkin_id={data[index].id}
                                        isAuthenticated={isAuthenticated}
                                    />
                                )
                            )}
                            {data.length < subscription.plan_classes && isAuthenticated && (
                                <Checkbox
                                    style="add"
                                    subscription_id={subscription.id}
                                    setCheckInModalIsOpen={
                                        setCheckInModalIsOpen
                                    }
                                    setSubscriptionToCancel={
                                        setSubscriptionToCancel
                                    }
                                />
                            )}
                            {data.length < subscription.plan_classes && unChecked > 0 &&
                                [...Array(unChecked)].map(
                                    (elementInArray, index) => (
                                        <Checkbox key={index} />
                                    )
                                )}
                        </div>
                    )}
                    {isAuthenticated && (
                        <Menu
                            as="div"
                            className="flex-shrink-0 pr-2 absolute right-4 top-6"
                        >
                            <Menu.Button className="w-8 h-8 bg-white inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
                                <span className="sr-only">Open options</span>
                                <DotsVerticalIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                />
                            </Menu.Button>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="z-10 mx-3 origin-top-right absolute right-2 top-8 w-48 mt-1 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 focus:outline-none">
                                    <div className="py-1">
                                        <Menu.Item>
                                            {({ active }) => (
                                                <button
                                                    className={classNames(
                                                        active
                                                            ? "bg-gray-100 text-gray-900"
                                                            : "text-gray-700",
                                                        "block px-4 py-2 text-sm w-full text-left"
                                                    )}
                                                    onClick={() => {
                                                        setSubscriptionToCancel(
                                                            subscription.id
                                                        );
                                                        setCancelSubModalIsOpen(
                                                            true
                                                        );
                                                    }}
                                                >
                                                    Cancel Subscription
                                                </button>
                                            )}
                                        </Menu.Item>
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    )}
                </div>
            </div>
        </>
    );
}
